/**
 * @module fx/ScreenShake
 * Screen shake — offsets the game camera by a decaying random amount
 * on significant events (slash, bomb, frenzy start).
 *
 * The render layer reads {@link ScreenShake.offset} / calls {@link triggerShake}
 * and applies the CSS transform to the game container element each frame.
 *
 * Functional API (contract): triggerShake(), cancelShake().
 * Legacy class API (ScreenShake) is kept for existing wiring.
 */

import { CONFIG } from '../game-config.js';

/** 2-D camera offset in pixels. */
export interface ShakeOffset {
  x: number;
  y: number;
}

/** Manages a decaying screen-shake effect. */
export class ScreenShake {
  private _intensity = 0;
  private _duration = 0;
  private _elapsed = 0;

  /**
   * Triggers a shake with the given intensity (peak offset magnitude in px)
   * and duration (ms). Multiple overlapping shakes are additive up to a cap.
   */
  trigger(intensity: number, durationMs = 200): void {
    this._intensity = Math.min(this._intensity + intensity, intensity * 1.5);
    this._duration = durationMs;
    this._elapsed = 0;
  }

  /** Advances the shake timer. Call once per render frame. */
  update(deltaMs: number): void {
    if (!this.active) return;
    this._elapsed += deltaMs;
    if (this._elapsed >= this._duration) {
      this._intensity = 0;
    }
  }

  /** Current pixel offset to apply to the stage. */
  get offset(): ShakeOffset {
    if (!this.active) return { x: 0, y: 0 };
    const decay = 1 - this._elapsed / this._duration;
    const mag = this._intensity * decay;
    return {
      x: (Math.random() * 2 - 1) * mag,
      y: (Math.random() * 2 - 1) * mag,
    };
  }

  /** Whether a shake is currently in progress. */
  get active(): boolean {
    return this._intensity > 0 && this._elapsed < this._duration;
  }
}

// ---------------------------------------------------------------------------
// Functional module API (per module contract)
// ---------------------------------------------------------------------------

/**
 * Trigger a screen shake via CSS transform on the game container element.
 * intensity: max displacement in px. durationMs: total shake duration.
 * Respects prefers-reduced-motion: no-ops if motion is reduced.
 *
 * Uses a rAF loop to animate the transform; calls cancelShake() when done.
 */
export function triggerShake(
  containerEl: HTMLElement,
  intensity = CONFIG.feel.screenShakeIntensity,
  durationMs = CONFIG.feel.screenShakeDuration,
): void {
  // Respect prefers-reduced-motion.
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const startTime = performance.now();

  function animate(now: number): void {
    const elapsed = now - startTime;
    if (elapsed >= durationMs) {
      containerEl.style.transform = '';
      return;
    }
    const decay = 1 - elapsed / durationMs;
    const mag = intensity * decay;
    const dx = (Math.random() * 2 - 1) * mag;
    const dy = (Math.random() * 2 - 1) * mag;
    containerEl.style.transform = `translate(${dx}px, ${dy}px)`;
    requestAnimationFrame(animate);
  }

  requestAnimationFrame(animate);
}

/**
 * Immediately cancel any in-progress shake and reset transform to identity.
 */
export function cancelShake(containerEl: HTMLElement): void {
  containerEl.style.transform = '';
}
